import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import { get } from 'lodash';
import React from 'react';

import type { LiveCasinoGame } from '@swf/core/types/CasinoGame';

import One from '../../assets/one-plus.svg';
import Dice from '../../assets/playtech-dice.svg';
import Two from '../../assets/playtech-two.svg';
import MinusTwo from '../../assets/two.svg';

import styles from './LiveCasinoGameStatus.scss';
import RouletteStatus from './RouletteStatus';

interface Props {
  game: LiveCasinoGame;
  full: boolean;
  className?: string;
}

export interface SubProps extends Props {
  full: boolean;
}

const icons: Record<string, JSX.Element> = {
  '1': <One />,
  '2': <Two />,
  d: <Dice />,
  '-2': <MinusTwo />
};

export enum GameTypes {
  Blackjack,
  Roulette,
  Gameshow
}

export const getType = (game: LiveCasinoGame) => {
  switch (game.variant) {
    case 12:
    case 27:
    case 42:
      return game.gametype === 1002 ? GameTypes.Blackjack : null;
    case 9:
    case 15:
    case 17:
    case 39:
    case 52:
    case 41:
    case 45:
    case 53:
      return game.gametype === 1002 ? GameTypes.Roulette : null;
    default:
      return game.gametype === 1010 ? GameTypes.Gameshow : null;
  }
};
export const hasStatus = (game: LiveCasinoGame) => {
  const type = getType(game);
  if (type !== null) {
    switch (type) {
      case GameTypes.Gameshow:
        return game.statusMeta.show;
      case GameTypes.Roulette:
        return game.statusMeta.results;
    }
  }
  return false;
};

const GameshowStatus: React.FC<SubProps> = ({ className, full, game }) => (
  <div className={classNames(className, styles.gameshow)}>
    {(game?.statusMeta?.show || []).map((value, index) => (
      <div key={index} className={styles.token}>
        {icons[value]}
      </div>
    ))}
  </div>
);

const MetaMessage: React.FC<Props> = ({ game, full, className = '' }) => {
  const metaList = get(game, ['statusMeta', 'results'], []);
  const firstSet: string[] = [];
  const secondSet: string[] = [];
  const rouletteFeedEnabled = useFeature(FeatureFlag.CasinoLiveRoulette);

  switch (getType(game)) {
    case GameTypes.Roulette:
      if (metaList.length === 0) {
        return null;
      }

      if (metaList[0].indexOf && metaList[0].indexOf('|') !== -1) {
        metaList.forEach((val: string) => {
          const pieces = val.split('|');
          firstSet.push(pieces[0]);
          pieces.length > 1 && secondSet.push(pieces[1]);
        });
      } else {
        firstSet.push(...metaList);
      }

      return rouletteFeedEnabled && game.statusMeta.results ? (
        <>
          {
            <RouletteStatus
              className={classNames(className, styles.roulette)}
              initial={firstSet}
              count={firstSet.length}
            />
          }
          {secondSet.length > 0 && (
            <RouletteStatus count={0} className={className} initial={secondSet} />
          )}
        </>
      ) : null;
    case GameTypes.Gameshow:
      return <GameshowStatus game={game} full={full} className={className} />;
    default:
      return null;
  }
};

export default MetaMessage;
